import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { getAiChatApiBase } from '../../aiChat/config';
import {
  listAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  getAiChatAgentRequestErrorMessage,
  parseAgentDeleteBlockedByProjectsPayload,
} from '../../aiChat/api/agentsAdminApi';

const ENV_ALERT =
  'Set REACT_APP_AI_CHAT_API_BASE (for example http://localhost:3001/api/v1) to manage chatbot agents.';

const DELETE_BLOCKED_HINT =
  'Update the agent on these projects (in the listed modules), then try deleting again.';

function AiConfigSetting() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addVisible, setAddVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [deleteBlockedModal, setDeleteBlockedModal] = useState(null);
  // { agentId, displayNameDraft } | null — inline display name edit in table
  const [inlineEdit, setInlineEdit] = useState(null);
  const apiBase = getAiChatApiBase();

  const load = useCallback(async () => {
    if (!apiBase) {
      return;
    }
    setLoading(true);
    try {
      const data = await listAgents();
      setRows(data);
    } catch (e) {
      message.error(
        getAiChatAgentRequestErrorMessage(e, 'Could not load agents.'),
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (apiBase) {
      load();
    }
  }, [apiBase, load]);

  const openAdd = () => {
    setInlineEdit(null);
    addForm.resetFields();
    setAddVisible(true);
  };

  const submitAdd = () =>
    addForm
      .validateFields()
      .then((v) =>
        createAgent({
          name: v.name.trim(),
          displayName: v.displayName.trim(),
        }),
      )
      .then(() => {
        message.success('Agent created.');
        setAddVisible(false);
        return load();
      })
      .catch((e) => {
        if (e?.errorFields) {
          return Promise.reject(e);
        }
        message.error(
          getAiChatAgentRequestErrorMessage(e, 'Could not create agent.'),
        );
        return Promise.reject(e);
      });

  const beginInlineEdit = useCallback((record) => {
    setInlineEdit({
      agentId: record.id,
      displayNameDraft: record.displayName || '',
    });
  }, []);

  const cancelInlineEdit = useCallback(() => setInlineEdit(null), []);

  const saveInlineEdit = useCallback(async () => {
    if (!inlineEdit) {
      return;
    }
    const trimmed = inlineEdit.displayNameDraft.trim();
    if (!trimmed) {
      message.warning('Display name is required.');
      return;
    }
    const record = rows.find((r) => r.id === inlineEdit.agentId);
    if (!record) {
      setInlineEdit(null);
      return;
    }
    try {
      await updateAgent(record.id, {
        name: record.name,
        displayName: trimmed,
      });
      message.success('Agent updated.');
      setInlineEdit(null);
      await load();
    } catch (e) {
      message.error(
        getAiChatAgentRequestErrorMessage(e, 'Could not update agent.'),
      );
    }
  }, [inlineEdit, rows, load]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        await deleteAgent(id);
        message.success('Agent deleted.');
        setInlineEdit((prev) => (prev?.agentId === id ? null : prev));
        await load();
      } catch (e) {
        const blocked = parseAgentDeleteBlockedByProjectsPayload(e);
        if (blocked) {
          setDeleteBlockedModal(blocked);
          return;
        }
        message.error(
          getAiChatAgentRequestErrorMessage(e, 'Could not delete agent.'),
        );
      }
    },
    [load],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Display name',
        key: 'displayName',
        width: '28%',
        render: (_, record) => {
          const isRowEditing = inlineEdit?.agentId === record.id;
          if (isRowEditing) {
            return (
              <Input
                className="ai-agents-display-name-input"
                value={inlineEdit.displayNameDraft}
                onChange={(e) =>
                  setInlineEdit((prev) =>
                    prev && prev.agentId === record.id
                      ? { ...prev, displayNameDraft: e.target.value }
                      : prev,
                  )
                }
                onPressEnter={() => saveInlineEdit()}
              />
            );
          }
          return (
            <span className="ai-agents-display-name-text">
              {record.displayName}
            </span>
          );
        },
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: '18%',
        className: 'ai-agents-col-system-name',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 220,
        align: 'right',
        render: (_, record) => {
          const isRowEditing = inlineEdit?.agentId === record.id;
          const anotherRowEditing =
            inlineEdit != null && inlineEdit.agentId !== record.id;

          if (isRowEditing) {
            return (
              <Space size="small">
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={saveInlineEdit}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={cancelInlineEdit}
                >
                  Cancel
                </Button>
              </Space>
            );
          }

          return (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                disabled={anotherRowEditing}
                onClick={() => beginInlineEdit(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Delete this agent?"
                okText="Delete"
                okType="danger"
                onConfirm={() => handleDelete(record.id)}
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={anotherRowEditing}
                >
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [
      inlineEdit,
      beginInlineEdit,
      cancelInlineEdit,
      saveInlineEdit,
      handleDelete,
    ],
  );

  return (
    <div className="ai-config-setting manage-module-wrapper">
      {!apiBase ? (
        <Alert
          type="warning"
          message="Chatbot API base URL not configured"
          description={ENV_ALERT}
          showIcon
        />
      ) : (
        <>
          <div className="ai-agents-page-intro">
            <h2 className="ai-agents-page-title">AI agent management</h2>
            <p className="ai-agents-page-subtitle">
              Manage system agents for chat configuration. After an agent is
              created, only the display name can be changed here; the system name
              stays fixed.
            </p>
          </div>
          <div className="ai-agents-toolbar">
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                Add agent
              </Button>
            </Space>
          </div>
          <div className="ai-agents-table-card">
            <div className="ai-agents-table-wrap">
              <Table
                loading={loading}
                rowKey="id"
                columns={columns}
                dataSource={rows}
                pagination={false}
                bordered
                size="middle"
                className="dd-table ai-agents-table"
                locale={{ emptyText: 'No agents configured yet.' }}
              />
            </div>
          </div>
          <Modal
            title={
              <>
                <PlusOutlined className="ai-agent-add-modal-title-icon" />{' '}
                <span>Add agent</span>
              </>
            }
            visible={addVisible}
            onOk={submitAdd}
            onCancel={() => setAddVisible(false)}
            destroyOnClose
            wrapClassName="ai-agent-add-modal-wrap"
            width={600}
            okText="Save"
            cancelText="Cancel"
            okButtonProps={{ icon: <SaveOutlined /> }}
            cancelButtonProps={{ icon: <CloseOutlined /> }}
            bodyStyle={{
              padding: '1.25rem 1.5rem',
            }}
          >
            <div className="ai-agent-add-modal">
              <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Define the agent once"
                description="The system name is fixed after save. Display name can be edited later in the table and is what appears in configuration pickers."
                className="ai-agent-add-alert"
              />
              <Form
                form={addForm}
                layout="vertical"
                className="ai-agent-add-form"
                requiredMark
              >
                <Form.Item
                  name="name"
                  label="Name"
                  extra="Stable system identifier (e.g. for Foundry / APIs)."
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input
                    className="ai-agent-add-input"
                    placeholder="e.g. my-service-agent"
                    autoComplete="off"
                  />
                </Form.Item>
                <Form.Item
                  name="displayName"
                  label="Display name"
                  extra="Shown in dropdowns when admins pick an agent."
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input
                    className="ai-agent-add-input"
                    placeholder="Label shown in the UI"
                    autoComplete="off"
                  />
                </Form.Item>
              </Form>
            </div>
          </Modal>
          <Modal
            title="Cannot delete agent"
            visible={Boolean(deleteBlockedModal)}
            onCancel={() => setDeleteBlockedModal(null)}
            wrapClassName="ai-agent-delete-blocked-modal-wrap"
            footer={
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => setDeleteBlockedModal(null)}
              >
                OK
              </Button>
            }
            width={720}
            destroyOnClose
            bodyStyle={{
              maxHeight: '70vh',
              overflowY: 'auto',
              padding: '1.25rem 1.5rem',
            }}
          >
            {deleteBlockedModal ? (
              <div className="ai-agent-delete-blocked-modal">
                <Alert
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  message={deleteBlockedModal.message}
                  className="ai-agent-delete-blocked-alert"
                />
                <p className="ai-agent-delete-blocked-hint">
                  {DELETE_BLOCKED_HINT}
                </p>
                <Divider plain className="ai-agent-delete-blocked-divider">
                  Affected projects
                </Divider>
                <div className="ai-agent-delete-blocked-list">
                  {deleteBlockedModal.projects.map((p, idx) => (
                    <div
                      className="ai-agent-delete-blocked-project-card"
                      key={`${idx}-${p.projectName}`}
                    >
                      <div className="ai-agent-delete-blocked-project-header">
                        <span className="ai-agent-delete-blocked-project-name">
                          {p.projectName}
                        </span>
                        <Tag className="ai-agent-delete-blocked-count-tag">
                          {p.moduleCount}{' '}
                          {p.moduleCount === 1 ? 'module' : 'modules'}
                        </Tag>
                      </div>
                      <div className="ai-agent-delete-blocked-module-tags">
                        {p.modules.map((m) => (
                          <Tag
                            key={`${idx}-${p.projectName}-${m}`}
                            className="ai-agent-delete-blocked-module-tag"
                          >
                            {m}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </Modal>
        </>
      )}
    </div>
  );
}

export default memo(AiConfigSetting);
